import { Signaler } from './peer.connection';
import { Stream } from '../stream';

const ICE_COMPLETE_TIMEOUT = 5 * 1000;

export class IceManager extends Stream<'iceComplete' | 'iceTimeout' | 'iceStateChange' | 'iceReady'> {
  private readonly pendingCandidates: RTCIceCandidateInit[] = [];
  private iceComplete = false;
  private completionTimer: any;
  private hasEmittedReady = false;

  constructor(
    private readonly peerConnection: RTCPeerConnection,
    private readonly trickle: boolean,
    private readonly signaler: Signaler,
  ) {
    super();
    peerConnection.addEventListener('icecandidate', this.onIceCandidate);
    peerConnection.addEventListener('iceconnectionstatechange', this.onIceStateChange);
    peerConnection.addEventListener('icegatheringstatechange', this.onIceGatheringStateChange);
    peerConnection.addEventListener('connectionstatechange', this.onConnectionStateChange);
  }

  public isIceComplete() {
    return this.iceComplete;
  }

  private onConnectionStateChange = () => {
    if (this.isDestroyedOrDestroying()) return;

    if (this.peerConnection.connectionState === 'failed') {
      this.destroy('Connection failed');
    }
  };

  private onIceCandidate = (event: RTCPeerConnectionIceEvent) => {
    if (event.candidate && this.trickle) {
      this.signaler({
        type: 'candidate',
        candidate: event.candidate.toJSON(),
      });
    } else if (!event.candidate && !this.iceComplete) {
      clearTimeout(this.completionTimer);
      this.iceComplete = true;
      this.emit('iceComplete');

      // FIX: Standard-compliant End-of-Candidates signal
      if (this.trickle) {
        this.signaler({
          type: 'candidate',
          candidate: { candidate: '', sdpMid: null, sdpMLineIndex: null },
        });
      }
    }
  };

  public onCandidateSignal = (candidate: RTCIceCandidateInit) => {
    if (this.peerConnection.remoteDescription && this.peerConnection.remoteDescription.type) {
      this.addIceCandidate(candidate);
    } else {
      this.pendingCandidates.push(candidate);
    }
  };

  private onIceGatheringStateChange = () => {
    if (this.isDestroyedOrDestroying()) return;

    const { iceGatheringState } = this.peerConnection;

    if (iceGatheringState === 'gathering') {
      this.startIceCompleteTimeout();
    } else if (iceGatheringState === 'complete' && !this.iceComplete) {
      clearTimeout(this.completionTimer);
      this.iceComplete = true;
      this.emit('iceComplete');
    }

    this.onIceStateChange();
  };

  private onIceStateChange = () => {
    if (this.isDestroyedOrDestroying()) return;

    const { iceConnectionState, iceGatheringState } = this.peerConnection;

    this.emit('iceStateChange', iceConnectionState, iceGatheringState);

    if (iceConnectionState === 'connected' || iceConnectionState === 'completed') {
      if (!this.hasEmittedReady) {
        this.hasEmittedReady = true;
        this.emit('iceReady');
      }
    }
    if (iceConnectionState === 'failed') {
      this.destroy('Ice connection failed');
    }
    if (iceConnectionState === 'closed') {
      this.destroy('Ice connection closed');
    }
  };

  public onRemoteDescription() {
    if (this.isDestroyedOrDestroying()) return;

    this.pendingCandidates.forEach((candidate) => {
      this.addIceCandidate(candidate);
    });
    this.pendingCandidates.length = 0;
  }

  // REWRITTEN FIX: Defensive instantiation and End-of-Candidates translation
  private addIceCandidate(candidateInit: RTCIceCandidateInit) {
    if (this.isDestroyedOrDestroying()) return;

    let rtcCandidate: RTCIceCandidate | undefined;

    // Check for valid candidate string to avoid instantiating end-of-candidate signal
    if (candidateInit && typeof candidateInit.candidate === 'string' && candidateInit.candidate !== '') {
      try {
        rtcCandidate = new RTCIceCandidate(candidateInit);
      } catch (e) {
        console.warn('[IceManager] Failed to construct RTCIceCandidate:', e);
        return;
      }
    }

    // Passing 'undefined' (rtcCandidate) here is the standard way to signal end-of-candidates
    this.peerConnection.addIceCandidate(rtcCandidate).catch((err) => {
      console.warn('[IceManager] Non-fatal error adding ICE candidate:', err.message);
    });
  }

  private startIceCompleteTimeout() {
    if (this.isDestroyedOrDestroying() || this.completionTimer) return;

    this.completionTimer = setTimeout(() => {
      if (!this.iceComplete) {
        this.iceComplete = true;
        this.emit('iceTimeout');
        this.emit('iceComplete');
      }
    }, ICE_COMPLETE_TIMEOUT);
  }

  protected onDestroy() {
    this.peerConnection.removeEventListener('icecandidate', this.onIceCandidate);
    this.peerConnection.removeEventListener('iceconnectionstatechange', this.onIceStateChange);
    this.peerConnection.removeEventListener('icegatheringstatechange', this.onIceGatheringStateChange);
    this.peerConnection.removeEventListener('connectionstatechange', this.onConnectionStateChange);

    clearTimeout(this.completionTimer);
    this.completionTimer = undefined;

    this.pendingCandidates.length = 0;
  }
}
